/**
 * 69.61 step 3 (DIRECTION §0.13's consequences): what a fallen objective changes, told as it lands. The systems already carry
 * the effects — a side that has seen an air-defence site go (or its generator) no longer counts it as cover, so its calls
 * are not refused and its routes not bent there (core/mountIntel.ts, core/skyRoute.ts); a fallen FOB refills, feeds, spawns
 * and links nothing (every reader asks for it standing) — and these are the moments they land, as events the HUD tells
 * (ui/groundHudView.ts consequenceBeat):
 *   - **the sky opened** (`skyOpened`): an enemy air-defence site left a side's known cover — the side saw it go, or saw its
 *     generator down. Fog-honest: the side that has not looked still believes it stands, and is told nothing.
 *   - **a FOB lost** (`fobLost`): a side's FOB standing at the tick's start is gone at its end — destroyed, or taken (69.4
 *     step 6d's sector rule, or its capture) — its stock and its link with it.
 *   - **an HQ lost** (`hqLost`): a side's HQ standing at the tick's start is down at its end, where the fall bites (core/hqFall.ts
 *     hqFallBites: the board's points battle, not a push) — the side's arrivals stop (and its tickets bleed, HQ_FALL; 69.5, decision 165).
 * Only with GameConfig.storyObjectives (the story layer): off, no event, nothing moved. Pure.
 */
import type { GameState, StructureState } from '../types';
import { knownMounts } from './mountIntel';
import { isSite } from './pointDefence';
import { structureTeam } from './body';
import { hqFallBites } from './hqFall';

export type ConsequenceEvent =
  | { kind: 'skyOpened'; team: number; siteId: number }
  | { kind: 'fobLost'; team: number; structureId: number; taken: boolean }
  | { kind: 'hqLost'; team: number; structureId: number };

const teamsOf = (state: Pick<GameState, 'players'>) => [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);

/** The sites each side counted as cover before a step and does not after it: the sky over them opened, as the side knows it. */
export function skyOpened(before: GameState, after: GameState): ConsequenceEvent[] {
  if (!after.config.storyObjectives || before.mountsSeen === after.mountsSeen) return [];
  const out: ConsequenceEvent[] = [];
  for (const team of teamsOf(after)) {
    const now = new Set(knownMounts(after, team).filter(isSite).map(m => m.id));
    for (const m of knownMounts(before, team)) if (isSite(m) && !now.has(m.id)) out.push({ kind: 'skyOpened', team, siteId: m.id });
  }
  return out;
}

/** A FOB or a base's HQ standing, by id: its side, and whether it is the HQ (the tick's start, for `holdsLost`). */
export type StandingHolds = Map<number, { team: number; hq: boolean }>;

/** Each side's FOBs and HQs standing (the tick's start, for `holdsLost`); null with the story off. */
export function standingHolds(state: Pick<GameState, 'structures' | 'players' | 'config' | 'board'>): StandingHolds | null {
  if (!state.config.storyObjectives) return null;
  const hqs = new Map((state.board?.bases ?? []).filter(b => b.hq !== undefined).map(b => [b.hq!, b.team]));
  const out: StandingHolds = new Map();
  for (const s of state.structures) {
    if (s.health <= 0) continue;
    if (hqs.has(s.id)) out.set(s.id, { team: hqs.get(s.id)!, hq: true });
    else if (s.type === 'fob') { const t = structureTeam(state, s); if (t !== null) out.set(s.id, { team: t, hq: false }); }
  }
  return out;
}

/** The FOBs and HQs standing at the tick's start (`standingHolds`) that are gone (or, a FOB, another side's) at its end. */
export function holdsLost(atStart: StandingHolds | null, after: GameState): ConsequenceEvent[] {
  if (!atStart?.size) return [];
  const out: ConsequenceEvent[] = [];
  for (const [id, { team, hq }] of atStart) {
    const s: StructureState | undefined = after.structures.find(x => x.id === id);
    if (hq) { if ((!s || s.health <= 0) && hqFallBites(after, team)) out.push({ kind: 'hqLost', team, structureId: id }); continue; }
    const now = s && s.health > 0 ? structureTeam(after, s) : null;
    if (now !== team) out.push({ kind: 'fobLost', team, structureId: id, taken: now !== null });
  }
  return out;
}
